import { BalanceSchema } from '@/shared/schema';
import { increment, serverTimestamp } from 'firebase/firestore';
import { z } from 'zod';

export type BalanceDocument = z.infer<typeof BalanceSchema>;

// Helper type que soporta FieldValues (como increment y serverTimestamp)
export type BalanceUpdate = Partial<Record<keyof BalanceDocument, any>> &
  Record<string, any>;

export const BalanceService = {
  /**
   * 🏦 1. PHYSICAL PAYMENT UPDATE (Liquidity)
   * This is called EVERY time a payment is verified.
   * It tracks who is holding the physical money right now.
   */
  preparePaymentUpdate: (params: {
    amount_accounting: number;
    amount_local: number;
    currency: string;
    paymentMethodId: string;
    isIndependent: boolean;
    isMainVault: boolean;
  }) => {
    const {
      amount_accounting: amt,
      amount_local: local,
      paymentMethodId,
      isMainVault,
      currency,
    } = params;

    const updates: any = {
      last_updated: serverTimestamp(),
      method_balances: {
        [paymentMethodId]: increment(local),
      },
    };

    if (currency === 'BS') {
      updates.lifetime_native_bs_received = increment(local);
    }

    if (isMainVault) {
      // Money went to the Salon's bank/vault
      updates.lifetime_vault_received_accounting = increment(amt);
    }
    // We no longer decrement balance_accounting here.
    // The worker's accounting debt is settled at sale closure.

    return updates;
  },

  /**
   * 📊 2. REVENUE RELEASE (Business Ledger)
   * Triggered ONLY when a sale status moves to 'paid'.
   * This updates the Salon's P&L metrics.
   */
  prepareRevenueRelease: (params: {
    total_gross: number;
    commission: number;
    materials: number;
    isIndependent: boolean;
  }) => {
    const { total_gross, commission, materials } = params;

    return {
      last_updated: serverTimestamp(),
      period_gross_accounting: increment(total_gross),
      period_commissions_accounting: increment(commission),
      period_materials_accounting: increment(materials),
      // Net Profit = What stays in the shop after paying the worker and materials
      period_net_accounting: increment(total_gross - commission - materials),
    };
  },

  /**
   * 👷 3. WORKER EARNINGS RELEASE (Worker Ledger)
   * Triggered ONLY when a sale status moves to 'paid'.
   * This updates the worker's payroll and service stats.
   */
  prepareWorkerEarningsUpdate: (params: { commission: number }) => {
    return {
      last_updated: serverTimestamp(),
      // The Salon now "owes" the worker their commission
      balance_accounting: increment(params.commission),
      period_earned_accounting: increment(params.commission),
      lifetime_earned_accounting: increment(params.commission),
      period_services_count: increment(1),
      total_services_done: increment(1),
    };
  },

  /**
   * 🔄 2. TRANSFERENCIA INTERNA (Abonos entre Staff y Dueño)
   * Mueve dinero físico de un bolsillo a otro y salda la deuda contable.
   */
  prepareInternalTransfer: (params: {
    isWorkerPaying: boolean;
    amount_accounting: number;
    amount_local: number;
    paymentMethodId: string;
  }): { workerUpdates: BalanceUpdate; mainUpdates: BalanceUpdate } => {
    const {
      isWorkerPaying,
      amount_accounting: acc,
      amount_local: local,
      paymentMethodId,
    } = params;

    const workerUpdates: BalanceUpdate = {
      last_updated: serverTimestamp(),
      // Si el barbero paga, su deuda disminuye (+). Si cobra su comisión, su favor disminuye (-).
      balance_accounting: increment(isWorkerPaying ? acc : -acc),
      method_balances: {
        [paymentMethodId]: increment(isWorkerPaying ? -local : local),
      },
    };

    const mainUpdates: BalanceUpdate = {
      last_updated: serverTimestamp(),
      method_balances: {
        [paymentMethodId]: increment(isWorkerPaying ? local : -local),
      },
    };

    if (isWorkerPaying) {
      mainUpdates.lifetime_vault_received_accounting = increment(acc);
    }

    return { workerUpdates, mainUpdates };
  },

  /**
   * 🏠 3. CARGO DE RENTA (Silla Alquilada)
   * Impacta deuda del barbero y proyecta la ganancia del local.
   */
  prepareRentCharge: (
    amount_accounting: number,
  ): { workerUpdates: BalanceUpdate; mainUpdates: BalanceUpdate } => {
    return {
      workerUpdates: {
        last_updated: serverTimestamp(),
        balance_accounting: increment(-amount_accounting), // Deuda sube
        period_rent_debt_accounting: increment(amount_accounting),
        last_rent_charged: serverTimestamp(),
      },
      mainUpdates: {
        last_updated: serverTimestamp(),
        period_rent_income_accounting: increment(amount_accounting),
        period_net_accounting: increment(amount_accounting), // Va directo a utilidad
      },
    };
  },

  /**
   * 📈 4. MÉTRICAS DE VENTA DEL NEGOCIO (P&L Global)
   */
  prepareSaleUpdate: (params: {
    total_gross: number;
    commission: number;
    materials: number;
    isIndependent: boolean;
  }): BalanceUpdate => {
    const { total_gross, commission, materials, isIndependent } = params;
    const net_profit = total_gross - commission - materials;

    const updates: BalanceUpdate = {
      last_updated: serverTimestamp(),
      period_gross_accounting: increment(total_gross),
      period_net_accounting: increment(net_profit),
      period_commissions_accounting: increment(commission),
      period_materials_accounting: increment(materials),
    };

    if (isIndependent) {
      updates.period_earned_accounting = increment(net_profit);
      updates.lifetime_earned_accounting = increment(net_profit);
      updates.period_services_count = increment(1);
      updates.total_services_done = increment(1);
    }

    return updates;
  },

  /**
   * 💈 5. MÉTRICAS DE VENTA DEL BARBERO
   */
  prepareWorkerSaleUpdate: (commission_accounting: number): BalanceUpdate => {
    return {
      last_updated: serverTimestamp(),
      balance_accounting: increment(commission_accounting),
      period_earned_accounting: increment(commission_accounting),
      lifetime_earned_accounting: increment(commission_accounting),
      period_services_count: increment(1),
      total_services_done: increment(1),
    };
  },

  /**
   * 🧹 6. RESETEO DE PERIODO (Cierre de Semana)
   */
  prepareResetPeriodUpdate: (isMainVault: boolean): BalanceUpdate => {
    const updates: BalanceUpdate = {
      last_updated: serverTimestamp(),
      period_earned_accounting: 0,
      period_services_count: 0,
      period_materials_accounting: 0, // 👈 Se resetea para todos
      period_rent_debt_accounting: 0,
      method_balances: {}, // Usado dentro de transaction.update(), esto vacía el mapa correctamente
    };

    if (isMainVault) {
      updates.period_gross_accounting = 0;
      updates.period_net_accounting = 0;
      updates.period_commissions_accounting = 0;
      updates.period_materials_accounting = 0;
      updates.period_rent_income_accounting = 0;
      updates.period_expenses_accounting = 0;
    }

    return updates;
  },

  /**
   * 📉 7. REGISTRO DE EGRESO (Salida de dinero)
   * Afecta el saldo físico (method_balances) y suma al acumulador de gastos.
   */
  prepareExpense: (params: {
    amount_accounting: number;
    amount_local: number;
    paymentMethodId: string;
    isMainVault: boolean;
  }): BalanceUpdate => {
    const { amount_accounting, amount_local, paymentMethodId, isMainVault } = params;

    const updates: BalanceUpdate = {
      last_updated: serverTimestamp(),
      balance_accounting: increment(-amount_accounting),
      method_balances: {
        [paymentMethodId]: increment(-amount_local),
      },
    };

    if (isMainVault) {
      updates.period_expenses_accounting = increment(amount_accounting);
      updates.period_net_accounting = increment(-amount_accounting);
    }

    return updates;
  },
};
